import { FC } from 'react';
import { Box, MantineTheme, Menu, Stack, Text } from '@mantine/core';
import {
  IconCheck,
  IconEdit,
  IconRepeatOnce,
  IconX,
} from '@tabler/icons-react';
import { UseColorSchemeValue } from '@mantine/hooks';
import { claimTariffStatusEnumValues } from '@billing/validation/tariff';
import { useTranslation } from '@refinedev/core';

interface ClaimsContextMenuProps {
  contextMenuPosition: { x: number; y: number } | null;
  onClose: () => void;
  selectedTariffCount: number;
  selectedClaimCount: number;
  hasTariffSelection: boolean;
  hasPendingTariffSelection: boolean;
  hasCancellableClaimSelection: boolean;
  hasBulkEditableClaimSelection: boolean;
  hasReconcilableTariffSelection: boolean;
  hasResubmittableTariffSelection: boolean;
  onCancelSelectedClaims: () => void;
  onEditSelectedClaims: () => void;
  onResubmitSelectedTariffs: () => void;
  onReconcileSelectedTariffs: () => void;
  onMarkSelectedTariffs: (status: string) => void;
  onClearSelection: () => void;
  mode: UseColorSchemeValue;
  theme: MantineTheme;
}

export const ClaimsContextMenu: FC<ClaimsContextMenuProps> = ({
  contextMenuPosition,
  onClose,
  selectedTariffCount,
  selectedClaimCount,
  hasTariffSelection,
  hasPendingTariffSelection,
  hasCancellableClaimSelection,
  hasBulkEditableClaimSelection,
  hasReconcilableTariffSelection,
  hasResubmittableTariffSelection,
  onCancelSelectedClaims,
  onEditSelectedClaims,
  onResubmitSelectedTariffs,
  onReconcileSelectedTariffs,
  onMarkSelectedTariffs,
  onClearSelection,
  mode,
  theme,
}) => {
  const { translate } = useTranslation();

  return (
    <Menu
      withinPortal
      opened={!!contextMenuPosition}
      onClose={onClose}
      closeOnEscape
      closeOnClickOutside
      position="right-start"
      radius="md"
      shadow="xl"
      offset={4}
      transitionProps={{ transition: 'fade', duration: 120 }}
      styles={{
        dropdown: {
          padding: '0.375rem 0.5rem',
          backdropFilter: 'blur(6px)',
          background:
            mode === 'dark' ? 'rgba(35,38,42,0.85)' : 'rgba(255,255,255,0.85)',
          border: `1px solid ${
            mode === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]
          }`,
          boxShadow: theme.shadows.lg,
        },
        item: {
          borderRadius: theme.radius.sm,
          fontSize: '0.8125rem',
        },
        label: {
          fontSize: '0.6875rem',
          letterSpacing: 0.5,
          fontWeight: 600,
          opacity: 0.7,
        },
      }}
    >
      <Menu.Target>
        <Box
          style={{
            position: 'fixed',
            top: contextMenuPosition?.y ?? -9999,
            left: contextMenuPosition?.x ?? -9999,
            pointerEvents: 'none',
          }}
          aria-hidden
        />
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>
          <Stack gap={2}>
            <Text size="xs" fw={700} tt="uppercase" lh={1.1}>
              Bulk tariff actions
            </Text>
            <Text size="xs" c="dimmed">
              {selectedTariffCount === 1
                ? '1 selected tariff'
                : `${selectedTariffCount} selected tariffs`}
            </Text>
          </Stack>
        </Menu.Label>
        <Menu.Item
          leftSection={<IconX size={14} stroke={1.5} />}
          onClick={() => {
            onClearSelection();
            onClose();
          }}
        >
          Clear selection
        </Menu.Item>
        <Menu.Divider />
        <Menu.Sub>
          <Menu.Sub.Target>
            <Menu.Sub.Item
              leftSection={<IconCheck size={14} stroke={1.5} />}
              disabled={!hasTariffSelection || hasPendingTariffSelection}
              title={
                hasPendingTariffSelection
                  ? translate('pages:claims.pendingActionDisabled')
                  : undefined
              }
            >
              Mark selected rows as
            </Menu.Sub.Item>
          </Menu.Sub.Target>
          <Menu.Sub.Dropdown>
            {claimTariffStatusEnumValues.map((status) => (
              <Menu.Item
                key={status}
                disabled={!hasTariffSelection || hasPendingTariffSelection}
                onClick={() => onMarkSelectedTariffs(status)}
              >
                {status}
              </Menu.Item>
            ))}
          </Menu.Sub.Dropdown>
        </Menu.Sub>
        <Menu.Item
          leftSection={<IconCheck size={16} stroke={1.5} />}
          disabled={!hasReconcilableTariffSelection}
          aria-disabled={!hasReconcilableTariffSelection}
          onClick={onReconcileSelectedTariffs}
        >
          Reconcile selected rows
        </Menu.Item>
        <Menu.Item
          leftSection={<IconRepeatOnce size={16} stroke={1.5} />}
          disabled={!hasResubmittableTariffSelection}
          aria-disabled={!hasResubmittableTariffSelection}
          onClick={onResubmitSelectedTariffs}
        >
          Resubmit selected rows
        </Menu.Item>
        {selectedClaimCount > 0 ? (
          <>
            <Menu.Divider />
            <Menu.Label>
              <Stack gap={2}>
                <Text size="xs" fw={700} tt="uppercase" lh={1.1}>
                  Bulk claim actions
                </Text>
                <Text size="xs" c="dimmed">
                  {selectedClaimCount === 1
                    ? '1 selected claim'
                    : `${selectedClaimCount} selected claims`}
                </Text>
              </Stack>
            </Menu.Label>
            <Menu.Item
              color="red"
              leftSection={<IconX size={16} stroke={1.5} />}
              disabled={!hasCancellableClaimSelection}
              onClick={onCancelSelectedClaims}
            >
              Cancel selected claims
            </Menu.Item>
            <Menu.Item
              leftSection={<IconEdit size={16} stroke={1.5} />}
              disabled={!hasBulkEditableClaimSelection}
              aria-disabled={!hasBulkEditableClaimSelection}
              onClick={onEditSelectedClaims}
            >
              {translate(
                'pages:claims.bulkEdit.menuAction',
                'Bulk edit selected claims'
              )}
            </Menu.Item>
          </>
        ) : null}
      </Menu.Dropdown>
    </Menu>
  );
};
